import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalCloseX from "../../components/common/ModalCloseX";
import ConfirmModal from "../../components/modals/ConfirmModal";
import ReportHelpCard from "../../components/reports/ReportHelpCard";
import { useAuth } from "../../context/AuthContext";
import {
  exportEmailOptOutClients,
  getEmailOptInStats,
  searchEmailOptOutClients,
  updateEmailOptOutClientPreference,
  updateEmailOptOutClientStatus,
} from "../../api/marketingEmailOptIn";
import { getClientEmailPreferenceLink } from "../../api/emailPreferences";
import { getCurrentSalonId, getCurrentSalonName } from "../../utils/currentSalon";
import { formatDateTimeInTimeZone } from "../../utils/datetime";
import { formatPermissionError } from "../../utils/permissionErrorMessages";
import "../MarketingSectionCompact.css";

const STATUS_OPTIONS = [
  { value: "ALL", label: "All clients" },
  { value: "SUBSCRIBED", label: "Subscribed" },
  { value: "UNSUBSCRIBED", label: "Unsubscribed" },
  { value: "HARD_OPTOUT", label: "Hard opt-out" },
  { value: "NEVER_ASKED", label: "Never asked" },
  { value: "PENDING_OPT_IN", label: "Pending opt-in" },
  { value: "UNKNOWN", label: "Unknown" },
];

const CATEGORY_OPTIONS = [
  { value: "", label: "All preferences" },
  { value: "all_optional", label: "All optional emails" },
  { value: "feedback", label: "Feedback requests" },
  { value: "appointment_reminders", label: "Appointment reminders" },
  { value: "service_reminders", label: "Refill and service reminders" },
  { value: "promotions", label: "Promotions and salon updates" },
];

const PREFERENCE_STATE_OPTIONS = [
  { value: "", label: "Any preference state" },
  { value: "opted_out", label: "Opted out" },
  { value: "opted_in", label: "Opted in" },
];

const PAGE_SIZE_OPTIONS = [25, 50, 75, 100, 250];

const QUICK_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "unsubscribed", label: "Unsubscribed" },
  { value: "hard_optout", label: "Hard opt-out" },
  { value: "subscribed", label: "Subscribed" },
  { value: "recently_changed", label: "Recently changed" },
  { value: "missing_preference_data", label: "Missing preference data" },
];

const EMAIL_OPTOUT_ACCESS_FALLBACK =
  "Email preferences are not available for your role yet. Ask an owner or manager to turn on Manage email opt-outs and preferences if you need it.";

const helpSections = [
  {
    heading: "What this page does",
    bullets: [
      "Find clients who opted out of optional emails.",
      "Update a client's email preferences when they ask.",
      "Use hard opt-out only when a client should not receive optional salon emails.",
    ],
  },
  {
    heading: "Recommended workflow",
    bullets: [
      "Search by name or email.",
      "Review the current status and reason.",
      "Use preference toggles for a specific email type.",
      "Use Opt out, Hard opt-out, or Resubscribe for the full optional email status.",
    ],
  },
  {
    heading: "Reports",
    bullets: [
      "Open the Email Subscription Report to review totals, trends, and reasons.",
      "Use filters here when you need the client list behind a preference.",
    ],
  },
];

const formatStatus = (value) => {
  const text = String(value || "UNKNOWN").replace(/_/g, " ").toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const statusClassName = (status) => {
  const key = String(status || "").toUpperCase();
  if (key === "SUBSCRIBED") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (key === "UNSUBSCRIBED") return "bg-amber-50 text-amber-700 border-amber-200";
  if (key === "HARD_OPTOUT") return "bg-rose-50 text-rose-700 border-rose-200";
  if (key === "PENDING_OPT_IN") return "bg-blue-50 text-blue-700 border-blue-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
};

export default function EmailOptOuts() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const salonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);
  const salonTimeZone = user?.salon_timezone || "America/Toronto";

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [category, setCategory] = useState("");
  const [preferenceState, setPreferenceState] = useState("");
  const [quickFilter, setQuickFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [savingKey, setSavingKey] = useState("");
  const [copyingKey, setCopyingKey] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);

  const selectedCategoryLabel = useMemo(
    () =>
      CATEGORY_OPTIONS.find((option) => option.value === category)?.label ||
      "All preferences",
    [category]
  );
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const showingStart = total > 0 ? (page - 1) * pageSize + 1 : 0;
  const showingEnd = total > 0 ? Math.min(page * pageSize, total) : 0;

  const buildSearchPayload = useCallback(
    (overrides = {}) => ({
      salon_id: salonId,
      search: search.trim() || null,
      status,
      category: category || null,
      preference_state: preferenceState || null,
      quick_filter: quickFilter || null,
      page,
      page_size: pageSize,
      ...overrides,
    }),
    [salonId, search, status, category, preferenceState, quickFilter, page, pageSize]
  );

  const loadStats = useCallback(async () => {
    if (!salonId) return;
    try {
      const data = await getEmailOptInStats(salonId, token);
      setStats(data || null);
    } catch (err) {
      setStats(null);
    }
  }, [salonId, token]);

  const loadRows = useCallback(async () => {
    if (!salonId) return;
    setLoading(true);
    setError("");
    try {
      const data = await searchEmailOptOutClients(
        buildSearchPayload(),
        token
      );
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotal(Number(data?.total || 0));
    } catch (err) {
      setRows([]);
      setTotal(0);
      setError(formatPermissionError(err, "Unable to load email preferences."));
    } finally {
      setLoading(false);
    }
  }, [salonId, token, buildSearchPayload]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(""), 2600);
    return () => clearTimeout(timer);
  }, [notice]);

  const resetToFirstPage = () => setPage(1);

  const handleQuickFilterChange = (nextValue) => {
    setQuickFilter(nextValue);
    resetToFirstPage();
    if (!nextValue) {
      setStatus("ALL");
    } else if (nextValue === "unsubscribed") {
      setStatus("UNSUBSCRIBED");
    } else if (nextValue === "hard_optout") {
      setStatus("HARD_OPTOUT");
    } else if (nextValue === "subscribed") {
      setStatus("SUBSCRIBED");
    } else if (nextValue) {
      setStatus("ALL");
    }
  };

  const handleExportCsv = async () => {
    if (!salonId) return;
    setExporting(true);
    setError("");
    try {
      const response = await exportEmailOptOutClients(
        buildSearchPayload({ page: 1, page_size: 250 }),
        token
      );
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const disposition = response.headers?.["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || "email_opt_outs.csv";
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatPermissionError(err, "Unable to export email preferences."));
    } finally {
      setExporting(false);
    }
  };

  const openPreviewPage = () => {
    if (!salonId) return;
    const params = new URLSearchParams({
      salon_id: String(salonId),
      category: "all_optional",
    });
    window.open(`/email/unsubscribe/preview?${params.toString()}`, "_blank", "noopener");
  };

  const copyClientUnsubscribeLink = async (row) => {
    if (!salonId || !row?.client_id) return;
    const key = `${row.client_id}:link`;
    setCopyingKey(key);
    setError("");
    try {
      const data = await getClientEmailPreferenceLink({
        salonId,
        clientId: row.client_id,
        token,
        category: "all_optional",
      });
      const link = data?.url || `${window.location.origin}${data?.path || ""}`;
      if (!link) {
        throw new Error("Missing link");
      }
      await navigator.clipboard.writeText(link);
      setNotice("Client unsubscribe link copied.");
    } catch (err) {
      setError(formatPermissionError(err, EMAIL_OPTOUT_ACCESS_FALLBACK));
    } finally {
      setCopyingKey("");
    }
  };

  const updateRow = (clientId, updates) => {
    setRows((prev) =>
      prev.map((row) => (row.client_id === clientId ? { ...row, ...updates } : row))
    );
  };

  const handlePreferenceToggle = async (row, pref) => {
    const nextValue = !pref.is_subscribed;
    const key = `${row.client_id}:${pref.category}`;
    setSavingKey(key);
    setError("");
    try {
      const data = await updateEmailOptOutClientPreference(
        row.client_id,
        {
          salon_id: salonId,
          category: pref.category,
          is_subscribed: nextValue,
          reason: nextValue ? null : "Updated by salon",
        },
        token
      );
      updateRow(row.client_id, {
        preferences: data?.preferences || row.preferences,
        status: data?.status || row.status,
      });
      setNotice("Email preferences updated.");
      loadStats();
    } catch (err) {
      setError(formatPermissionError(err, EMAIL_OPTOUT_ACCESS_FALLBACK));
    } finally {
      setSavingKey("");
    }
  };

  const handleStatusUpdate = async () => {
    if (!confirmAction) return;
    const { row, nextStatus } = confirmAction;
    const key = `${row.client_id}:status`;
    setSavingKey(key);
    setError("");
    try {
      const data = await updateEmailOptOutClientStatus(
        row.client_id,
        {
          salon_id: salonId,
          status: nextStatus,
          reason: nextStatus === "SUBSCRIBED" ? null : "Updated by salon",
        },
        token
      );
      updateRow(row.client_id, {
        preferences: data?.preferences || row.preferences,
        status: data?.status || nextStatus,
        unsubscribed_at:
          nextStatus === "SUBSCRIBED" ? null : new Date().toISOString(),
        unsubscribe_reason:
          nextStatus === "SUBSCRIBED" ? null : "Updated by salon",
      });
      setNotice("Client email status updated.");
      setConfirmAction(null);
      loadStats();
    } catch (err) {
      setError(formatPermissionError(err, EMAIL_OPTOUT_ACCESS_FALLBACK));
    } finally {
      setSavingKey("");
    }
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate("/manager/schedule", { replace: true });
  };

  return (
    <div className="marketingSectionShell">
      <div className="marketingSectionWide marketing-section-tight marketing-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div>
              <div className="text-xl md:text-2xl font-semibold text-white">
                Email Opt-Outs
              </div>
              <div className="text-sm text-white/90">
                Manage unsubscribes and client email preferences.
              </div>
            </div>
            <div className="flex items-center gap-2 pr-10">
              <button
                type="button"
                onClick={() => navigate("/manager/reports/email-subscription")}
                className="rounded-full border border-white/70 px-4 py-2 text-xs font-semibold text-white hover:bg-white/10"
              >
                View Report
              </button>
              <button
                type="button"
                onClick={openPreviewPage}
                className="rounded-full border border-white/70 px-4 py-2 text-xs font-semibold text-white hover:bg-white/10"
              >
                Preview Public Page
              </button>
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <div className="text-sm text-slate-600">
              Salon: {salonName || "Unknown"} (ID {salonId || "--"})
            </div>

            <ReportHelpCard
              title="Email Opt-Outs"
              subtitle="Find clients who opted out and manage their email preferences."
              sections={helpSections}
            />

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}
            {notice && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                {notice}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              {[
                ["Clients with email", stats?.total_clients_with_email],
                ["Subscribed", stats?.subscribed_count],
                ["Unsubscribed", stats?.unsubscribed_count],
                ["Hard opt-out", stats?.hard_optout_count],
                ["Last 30 days", stats?.last_30d_unsubscribes],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                >
                  <div className="text-xs font-semibold uppercase text-slate-500">
                    {label}
                  </div>
                  <div className="mt-1 text-lg font-semibold text-slate-900">
                    {value ?? "--"}
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                {QUICK_FILTER_OPTIONS.map((option) => {
                  const active =
                    option.value === ""
                      ? quickFilter === "" && status === "ALL"
                      : quickFilter === option.value;
                  return (
                    <button
                      key={option.value || "all"}
                      type="button"
                      onClick={() => handleQuickFilterChange(option.value)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                        active
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.5fr_1fr_1fr_1fr_auto] lg:items-end">
                <label className="space-y-1">
                  <span className="text-xs font-semibold uppercase text-slate-500">
                    Search
                  </span>
                  <input
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      resetToFirstPage();
                    }}
                    className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                    placeholder="Name or email"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-semibold uppercase text-slate-500">
                    Status
                  </span>
                  <select
                    value={status}
                    onChange={(event) => {
                      setStatus(event.target.value);
                      setQuickFilter("");
                      resetToFirstPage();
                    }}
                    className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-semibold uppercase text-slate-500">
                    Preference
                  </span>
                  <select
                    value={category}
                    onChange={(event) => {
                      setCategory(event.target.value);
                      setPreferenceState("");
                      resetToFirstPage();
                    }}
                    className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                  >
                    {CATEGORY_OPTIONS.map((option) => (
                      <option key={option.value || "all"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-semibold uppercase text-slate-500">
                    Preference state
                  </span>
                  <select
                    value={preferenceState}
                    onChange={(event) => {
                      setPreferenceState(event.target.value);
                      resetToFirstPage();
                    }}
                    className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                    disabled={!category}
                  >
                    {PREFERENCE_STATE_OPTIONS.map((option) => (
                      <option key={option.value || "any"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  onClick={loadRows}
                  className="h-10 rounded-full bg-slate-900 px-5 text-xs font-semibold text-white"
                  disabled={loading}
                >
                  {loading ? "Loading..." : "Search"}
                </button>
              </div>
              <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="text-xs text-slate-500">
                  Showing {showingStart}-{showingEnd} of {total} clients. Preference filter: {selectedCategoryLabel}.
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                    Rows
                    <select
                      value={pageSize}
                      onChange={(event) => {
                        setPageSize(Number(event.target.value));
                        resetToFirstPage();
                      }}
                      className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-xs"
                    >
                      {PAGE_SIZE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={handleExportCsv}
                    className="h-9 rounded-full border border-slate-300 bg-white px-4 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    disabled={exporting || loading}
                  >
                    {exporting ? "Exporting..." : "Export CSV"}
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Client</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Unsubscribed</th>
                    <th className="px-3 py-2 text-left">Preferences</th>
                    <th className="px-3 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row) => (
                    <tr key={row.client_id} className="align-top">
                      <td className="px-3 py-3">
                        <div className="font-semibold text-slate-900">
                          {row.name || "Unnamed client"}
                        </div>
                        <div className="text-xs text-slate-500">{row.email}</div>
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/clients?search=${encodeURIComponent(row.email || row.name || "")}`)
                          }
                          className="mt-1 rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Open Client
                        </button>
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${statusClassName(
                            row.status
                          )}`}
                        >
                          {formatStatus(row.status)}
                        </span>
                        {row.unsubscribe_reason ? (
                          <div className="mt-2 max-w-[220px] text-xs text-slate-500">
                            Reason: {row.unsubscribe_reason}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 text-slate-600">
                        {row.unsubscribed_at
                          ? formatDateTimeInTimeZone(row.unsubscribed_at, salonTimeZone)
                          : "--"}
                      </td>
                      <td className="px-3 py-3">
                        <div className="grid min-w-[360px] gap-2 sm:grid-cols-2">
                          {(row.preferences || []).map((pref) => {
                            const key = `${row.client_id}:${pref.category}`;
                            const saving = savingKey === key;
                            return (
                              <button
                                key={pref.category}
                                type="button"
                                onClick={() => handlePreferenceToggle(row, pref)}
                                disabled={Boolean(savingKey)}
                                className={`flex min-h-[42px] items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left text-xs ${
                                  pref.is_subscribed
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                                    : "border-rose-200 bg-rose-50 text-rose-800"
                                }`}
                              >
                                <span className="font-semibold">{pref.label}</span>
                                <span className="shrink-0 rounded-full bg-white/80 px-2 py-1 font-semibold">
                                  {saving
                                    ? "Saving"
                                    : pref.is_subscribed
                                    ? "On"
                                    : "Off"}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex min-w-[220px] flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => copyClientUnsubscribeLink(row)}
                            disabled={copyingKey === `${row.client_id}:link`}
                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                          >
                            {copyingKey === `${row.client_id}:link`
                              ? "Copying..."
                              : "Copy link"}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setConfirmAction({ row, nextStatus: "UNSUBSCRIBED" })
                            }
                            disabled={savingKey === `${row.client_id}:status`}
                            className="rounded-full border border-amber-300 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-50"
                          >
                            Opt out
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setConfirmAction({ row, nextStatus: "HARD_OPTOUT" })
                            }
                            disabled={savingKey === `${row.client_id}:status`}
                            className="rounded-full border border-rose-300 px-3 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                          >
                            Hard opt-out
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setConfirmAction({ row, nextStatus: "SUBSCRIBED" })
                            }
                            disabled={savingKey === `${row.client_id}:status`}
                            className="rounded-full border border-emerald-300 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                          >
                            Resubscribe
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && rows.length === 0 && (
                    <tr>
                      <td
                        colSpan="5"
                        className="px-3 py-8 text-center text-sm text-slate-500"
                      >
                        No clients match these filters.
                      </td>
                    </tr>
                  )}
                  {loading && (
                    <tr>
                      <td
                        colSpan="5"
                        className="px-3 py-8 text-center text-sm text-slate-500"
                      >
                        Loading clients...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 md:flex-row md:items-center md:justify-between">
              <div className="text-xs text-slate-500">
                Showing {showingStart}-{showingEnd} of {total} clients
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage(1)}
                  disabled={loading || page <= 1}
                  className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  First
                </button>
                <button
                  type="button"
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  disabled={loading || page <= 1}
                  className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-2 text-xs font-semibold text-slate-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  disabled={loading || page >= totalPages}
                  className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
                <button
                  type="button"
                  onClick={() => setPage(totalPages)}
                  disabled={loading || page >= totalPages}
                  className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Last
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={Boolean(confirmAction)}
        title="Update email status?"
        message={
          confirmAction
            ? `${confirmAction.row.name || confirmAction.row.email} will be set to ${formatStatus(
                confirmAction.nextStatus
              )}.`
            : ""
        }
        confirmText={savingKey ? "Saving..." : "Update"}
        cancelText="Cancel"
        onClose={() => setConfirmAction(null)}
        onConfirm={handleStatusUpdate}
        isBusy={Boolean(savingKey)}
      />
    </div>
  );
}
